import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  SortingState
} from '@tanstack/react-table';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Pagination, Tag, Tooltip, Pill } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import { AxiosError } from 'axios';
import { niiService } from '../../services/niiService';
import { NiiRunType, NiiRunStatus, RunStatusResponse } from './types/nii.types';
import { useNiiRunWebSocket } from './hooks/useNiiWebSocket';
import styles from './NiiLandingPage.module.css';

// --- Helpers ---

function getDefaultName(type: NiiRunType): string {
  switch (type) {
    case NiiRunType.FEATURE_IMPACT:
      return 'Feature Impact';
    case NiiRunType.TR_IMPACT:
      return 'TR Impact';
    case NiiRunType.GPL_AUDIT:
      return 'GPL Audit';
    default:
      return 'Unknown';
  }
}

function getTypeLabel(type: NiiRunType): string {
  switch (type) {
    case NiiRunType.FEATURE_IMPACT:
      return 'Feature Impact';
    case NiiRunType.TR_IMPACT:
      return 'TR Impact';
    case NiiRunType.GPL_AUDIT:
      return 'GPL Audit';
    default:
      return type;
  }
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return iso;
  }
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  [NiiRunStatus.QUEUED]: { label: 'Queued', color: 'grey' },
  [NiiRunStatus.RUNNING]: { label: 'Running', color: 'blue' },
  [NiiRunStatus.COMPLETE]: { label: 'Complete', color: 'green' },
  [NiiRunStatus.ERROR]: { label: 'Failed', color: 'red' },
  [NiiRunStatus.CANCELLED]: { label: 'Cancelled', color: 'grey' }
};

const TYPE_PILLS: { label: string; value: NiiRunType }[] = [
  { label: 'TR Impact', value: NiiRunType.TR_IMPACT },
  { label: 'Feature Impact', value: NiiRunType.FEATURE_IMPACT },
  { label: 'GPL Audit', value: NiiRunType.GPL_AUDIT }
];

// --- Column helper ---

const columnHelper = createColumnHelper<RunStatusResponse>();

// --- Component ---

const ExecutionTable: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  // State
  const [activeTypes, setActiveTypes] = useState<NiiRunType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 6 });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Data fetching with polling
  // If exactly one type is selected, filter server-side. Otherwise fetch all and filter client-side.
  const serverType = activeTypes.length === 1 ? activeTypes[0] : undefined;

  const { data: runsData, isLoading, refetch } = useQuery({
    queryKey: ['nii-runs', activeTypes, pagination.pageIndex, pagination.pageSize],
    queryFn: () =>
      niiService.listRuns({
        type: serverType,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize
      }),
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    refetchInterval: (query) => {
      const queryData = query.state.data;
      const hasActive = (queryData?.data || []).some(
        (r) => r.status === NiiRunStatus.QUEUED || r.status === NiiRunStatus.RUNNING
      );
      return hasActive ? 10_000 : false;
    }
  });

  // Show global loader only on first load (no cached data yet)
  useEffect(() => {
    if (isLoading && !runsData) {
      showLoader();
    } else {
      hideLoader();
    }
    return () => hideLoader();
  }, [isLoading, runsData]);

  // Client-side type filter + search
  const runs = useMemo(() => {
    let filtered = runsData?.data || [];
    // Type filter
    if (activeTypes.length > 0 && activeTypes.length < 3) {
      filtered = filtered.filter((r) => activeTypes.includes(r.type));
    }
    // Global search (case-insensitive across name, type label, release, status)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((r) => {
        const name = (r.name || getDefaultName(r.type)).toLowerCase();
        const typeLabel = getTypeLabel(r.type).toLowerCase();
        const release = (r.releaseName || '').toLowerCase();
        const status = (r.status || '').toLowerCase();
        const createdBy = (r.createdBy || '').toLowerCase();
        return name.includes(q) || typeLabel.includes(q) || release.includes(q) || status.includes(q) || createdBy.includes(q);
      });
    }
    return filtered;
  }, [runsData, activeTypes, searchQuery]);
  const totalPages = runsData?.totalPages || 0;

  // WebSocket for instant status transitions
  const activeRunIds = useMemo(
    () =>
      runs
        .filter((r) => r.status === NiiRunStatus.QUEUED || r.status === NiiRunStatus.RUNNING)
        .map((r) => r.runId),
    [runs]
  );

  useNiiRunWebSocket(activeRunIds, () => {
    refetch();
  });

  // Handlers
  const handlePillClick = (type: NiiRunType) => {
    setActiveTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleViewDetails = async (run: RunStatusResponse) => {
    if (run.status !== NiiRunStatus.COMPLETE) return;

    // GPL audit runs → navigate to run detail page (shows nodes table)
    if (run.type === NiiRunType.GPL_AUDIT) {
      navigate(`/nii/audit-reports/run/${run.runId}`);
      return;
    }

    // Impact runs → fetch the report and navigate to content
    try {
      const reports = await niiService.getRunReports(run.runId);
      if (reports.length > 0) {
        const report = reports[0];
        if (report.type === NiiRunType.FEATURE_IMPACT) {
          navigate(`/nii/impact-reports/feature/${report.reportId}`);
        } else if (report.type === NiiRunType.TR_IMPACT) {
          navigate(`/nii/impact-reports/tr/${report.reportId}`);
        }
      }
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'Failed to load report details.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => {
        return row.name || getDefaultName(row.type);
      }, {
        id: 'name',
        header: 'Run Name',
        cell: (info) => info.getValue()
      }),
      columnHelper.accessor('type', {
        header: 'Audit Type',
        cell: (info) => getTypeLabel(info.getValue())
      }),
      columnHelper.accessor((row) => row.releaseName || row.gplVersion || '—', {
        id: 'release',
        header: 'Scripts Release',
        cell: (info) => info.getValue()
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          const config = STATUS_CONFIG[status] || STATUS_CONFIG[NiiRunStatus.QUEUED];
          return (
            <ReactEDSWrapper component={Tag} color={config.color}>
              {config.label}
            </ReactEDSWrapper>
          );
        }
      }),
      columnHelper.accessor('progress', {
        header: 'Progress',
        cell: (info) => {
          const row = info.row.original;
          if (row.status === NiiRunStatus.RUNNING) {
            const pct = row.progress != null ? Math.round(row.progress * 100) : 0;
            return (
              <span>
                <i className="icon icon-loading" style={{ marginRight: '4px' }} />
                {pct}%
              </span>
            );
          }
          if (row.status === NiiRunStatus.COMPLETE) {
            return <i className="icon icon-check" style={{ color: '#28a745' }} />;
          }
          if (row.status === NiiRunStatus.ERROR) {
            const errorDetail = row.error?.detail || 'Unknown error';
            return (
              <ReactEDSWrapper component={Tooltip} message={errorDetail} pointer>
                <i className="icon icon-triangle-warning" style={{ color: '#dc3545' }} />
              </ReactEDSWrapper>
            );
          }
          return null;
        }
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        cell: (info) => formatTimestamp(info.getValue())
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const row = info.row.original;
          const disabled = row.status !== NiiRunStatus.COMPLETE;
          return (
            <ReactEDSWrapper
              component={Button}
              onClick={() => handleViewDetails(row)}
              disabled={disabled}
            >
              View Details
            </ReactEDSWrapper>
          );
        }
      })
    ],
    []
  );

  // Table instance
  const table = useReactTable({
    data: runs,
    columns,
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
    state: {
      pagination,
      sorting
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel()
  });

  // Filter count text
  const filterApplied = activeTypes.length > 0;
  const itemCountText = `${runs.length} items${filterApplied ? ` · ${activeTypes.length} filter${activeTypes.length > 1 ? 's' : ''} applied` : ''}`;

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <div className={styles.settingsTitleContainer}>
          {/* Header */}
          <div className={styles.settingsTitle}>Executions</div>
          <p className={styles.subtitle}>Track the progress of all NII report generation runs.</p>

          {/* Pills + search row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {TYPE_PILLS.map((pill) => {
                let pillClass = 'toggle';
                if (activeTypes.length > 0) {
                  pillClass = activeTypes.includes(pill.value) ? 'toggle selected' : 'toggle unselected';
                }
                return (
                  <ReactEDSWrapper
                    key={pill.value}
                    component={Pill}
                    className={pillClass}
                    onClick={() => handlePillClick(pill.value)}
                  >
                    {pill.label}
                  </ReactEDSWrapper>
                );
              })}
            </div>
            <div style={{ position: 'relative', width: '250px' }}>
              <input
                type="text"
                className="input"
                placeholder="Search here"
                style={{ width: '100%', paddingRight: '32px' }}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i className="icon icon-search" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.6 }} />
            </div>
          </div>

          {/* Item count */}
          <div style={{ marginBottom: '8px', color: '#878787', fontSize: '12px' }}>
            {itemCountText}
            {filterApplied && (
              <span
                style={{ marginLeft: '8px', color: '#0069c2', cursor: 'pointer' }}
                onClick={() => setActiveTypes([])}
              >
                Clear all filters
              </span>
            )}
          </div>

          {/* Table */}
          {(
            <div className="table">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          style={{ textAlign: 'left', padding: '12px 16px', cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() && (
                              <span>
                                {header.column.getIsSorted() === 'asc' && <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }} />}
                                {header.column.getIsSorted() === 'desc' && <i className="icon icon-sort-down" />}
                                {!header.column.getIsSorted() && <i className="icon icon-sorting" />}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {runs.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '48px 16px' }}>
                        <i className="icon icon-info" style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }} />
                        <div>No executions found</div>
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        style={{ transition: 'background-color 0.2s ease' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 0 && (
            <ReactEDSWrapper
              component={Pagination}
              numPages={totalPages}
              selectedPage={pagination.pageIndex + 1}
              onPaginationChangePage={(page: number) => {
                setPagination((prev) => ({ ...prev, pageIndex: page - 1 }));
              }}
              optionsList={[
                { value: '6', label: '6', active: pagination.pageSize === 6 },
                { value: '10', label: '10', active: pagination.pageSize === 10 },
                { value: '20', label: '20', active: pagination.pageSize === 20 },
                { value: '50', label: '50', active: pagination.pageSize === 50 }
              ]}
              onPaginationChangeSelect={(size: string) => {
                setPagination({ pageSize: Number(size), pageIndex: 0 });
              }}
              withActions
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionTable;
